import type { Component } from 'vue';
import type { BlockDefinition } from '#layers/block-engine/types/block';
import { computed, defineComponent, h, inject, markRaw, onScopeDispose, ref, Suspense, toRaw, watch } from 'vue';
import { useBlockLibraryStore } from '#layers/block-engine/composables/block-editor/library';
import { isBlockDefinition } from '#layers/block-engine/utils/block/editor';
import { resolveBuiltinBlock } from '#layers/block-engine/utils/block/resolve';
import BlockErrorBoundary from './BlockErrorBoundary.vue';
import BlockRenderComponent from './BlockRenderComponent';
import BlockRenderSerialized from './BlockRenderSerialized';
import {
	blockPrivateRootContextFallbackKey,
	blockRenderEmits,
	blockRenderProps,
	blockRootContextApiFallbackKey,
	shouldRegisterBlockRootContext,
} from './BlockRenderShared';

export default defineComponent({
	name: 'BlockRender',

	props: blockRenderProps,

	emits: blockRenderEmits,

	inheritAttrs: false,

	setup(props, { slots, emit, attrs }) {
		if (!props.block) {
			throw new Error('BlockRender requires a "block" prop to be provided.');
		}

		const { onBlockRefresh } = useBlockLibraryStore();
		const errorBlockName = ref(typeof props.block === 'string' ? props.block : props.block.name);
		const blockIcon = ref<string | undefined>(undefined);
		const refreshKey = ref(0);
		const blockId = computed(() => {
			const block = props.block;

			if (!block) {
				return;
			}

			return typeof block === 'string' ? block : block.id;
		});
		const componentBlock = computed(() => {
			// Touch refreshKey so the computed re-runs when the block registry
			// signals a refresh for this id. Otherwise builtin definitions get
			// cached from the first resolution and stale markRaw'd references
			// leak across hot-module swaps.
			void refreshKey.value;

			if (!props.block) {
				return null;
			}

			if (typeof props.block === 'string') {
				const block = resolveBuiltinBlock(props.block);
				return block ? markRaw(toRaw(block)) : null;
			}

			return isBlockDefinition(props.block) ? markRaw(toRaw(props.block)) : null;
		});
		const syncComponentBlock = computed(() => {
			const block = componentBlock.value;

			if (!block || hasAsyncComponentLoader(block.comp)) {
				return null;
			}

			return block;
		});
		const suspenseComponentBlock = computed(() => {
			const block = componentBlock.value;

			if (!block || !hasAsyncComponentLoader(block.comp)) {
				return null;
			}

			return block;
		});

		const stopRefreshing = onBlockRefresh((refreshedBlockId) => {
			if (refreshedBlockId === blockId.value) {
				refreshKey.value++;
			}
		});

		onScopeDispose(() => {
			stopRefreshing.off();
		});

		const inheritedRootContextApi = inject(blockRootContextApiFallbackKey, null);

		// Fallback on the root context if not provided in case current RenderBlock is nested
		const getPrivateRootContextFallback = inject(blockPrivateRootContextFallbackKey, null);

		function getPrivateRootContext() {
			return {
				...getPrivateRootContextFallback?.(),
				...props.privateContext,
			};
		}

		const registerRootContext = computed(() => shouldRegisterBlockRootContext({
			registerRootContext: props.registerRootContext,
			inheritedRootContextApi,
		}));

		watch(componentBlock, (block) => {
			if (!block) {
				return;
			}

			errorBlockName.value = block.name;
			blockIcon.value = block.icon ?? undefined;
		}, {
			immediate: true,
		});

		// BlockRenderSerialized uses async setup. Without a local Suspense boundary,
		// Vue's fragment processing can crash during the patch cascade when the key
		// changes trigger unmount/remount and DOM anchors become stale mid-patch.
		function renderComponentBlock(block: BlockDefinition) {
			return h(BlockRenderComponent, {
				'key': `${blockId.value}:${refreshKey.value}`,
				...attrs,
				'block': block,
				'blockId': props.idOverride ?? blockId.value ?? block.id,
				'props': props.props,
				'spacing': props.spacing,
				'unwrapBlockDefinition': props.unwrapBlockDefinition,
				'context': props.context,
				'privateContext': getPrivateRootContext(),
				'registerRootContext': registerRootContext.value,
				'onBlock:load': (resolvedBlock) => {
					emit('block:load', resolvedBlock);
				},
				'onBlock:ref': (payload) => {
					emit('block:ref', payload);
				},
				'onBlock:resolve': (resolvedBlock) => {
					errorBlockName.value = resolvedBlock.name;
					blockIcon.value = resolvedBlock.icon ?? undefined;
					emit('block:resolve', resolvedBlock);
				},
			}, slots);
		}

		const renderBlock = () => {
			if (syncComponentBlock.value) {
				// Hot paths such as table cells mostly render component blocks. Keep
				// this branch sync-only; async component definitions still need a local
				// Suspense boundary and are routed below.
				return renderComponentBlock(syncComponentBlock.value);
			}

			if (suspenseComponentBlock.value) {
				return h(Suspense, null, {
					default: () => renderComponentBlock(suspenseComponentBlock.value!),
				});
			}

			return h(Suspense, null, {
				default: () => h(BlockRenderSerialized, {
					'key': `${blockId.value}:${refreshKey.value}`,
					...props,
					...attrs,
					'context': props.context,
					'privateContext': getPrivateRootContext(),
					'registerRootContext': registerRootContext.value,
					'onBlock:load': (block) => {
						emit('block:load', block);
					},
					'onBlock:ref': (payload) => {
						emit('block:ref', payload);
					},
					'onBlock:resolve': (block) => {
						errorBlockName.value = block.name;
						blockIcon.value = block.icon ?? undefined;
						emit('block:resolve', block);
					},
				}, slots),
			});
		};

		return () => props.skipErrorBoundary
			? renderBlock()
			: h(BlockErrorBoundary, {
					name: errorBlockName.value,
					icon: blockIcon.value,
				}, renderBlock);
	},
});

/**
 * Vue marks `defineAsyncComponent()` wrappers with an internal loader fn.
 */
function hasAsyncComponentLoader(component: Component) {
	if (!component || typeof component !== 'object') {
		return false;
	}

	return '__asyncLoader' in component && typeof component.__asyncLoader === 'function';
}
